/* import __COLOCATED_TEMPLATE__ from './brands.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type ApplicationInstance from '@ember/application/instance';
import { tracked } from '@glimmer/tracking';
import { request } from 'embercom/lib/inbox/requests';
import type NotificationsService from 'embercom/services/notifications-service';
import type IntlService from 'embercom/services/intl';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import { type TaskGenerator } from 'ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';
import { dropTask } from 'ember-concurrency-decorators';

interface Args {
  hideBorder?: boolean;
}

interface Signature {
  Args: Args;
  Element: HTMLDivElement;
}

interface Brand {
  id: string;
  name: string;
  logoId?: string;
  logoUrl?: string;
  helpCenterId?: string;
  senderEmailAddressSettingsId?: string;
  isDefault: boolean;
}

interface BrandWireFormat {
  id: string;
  name: string;
  logo_id?: string;
  logo_url?: string;
  help_center_id?: string;
  sender_email_address_settings_id?: string;
  is_default: boolean;
}

export default class ZendeskBrands extends Component<Signature> {
  @service declare finStandaloneService: FinStandaloneService;
  @service declare appService: AppService;
  @service declare notificationsService: InstanceType<typeof NotificationsService>;
  @service declare intl: IntlService;

  @tracked brands: Brand[] = [];

  constructor(owner: ApplicationInstance, args: Args) {
    super(owner, args);
    taskFor(this.loadBrands).perform();
  }

  get zendeskConfig() {
    return this.finStandaloneService.zendeskConfig;
  }

  get isLoadingZendeskBrands(): boolean {
    return taskFor(this.loadBrands).isRunning;
  }

  get synchronizedBrands(): Brand[] {
    return this.brands.filter((brand) => !brand.isDefault);
  }

  @dropTask
  *loadBrands(): TaskGenerator<Brand[]> {
    if (this.zendeskConfig?.isPendingSetupForTickets) {
      this.brands = [];
      return [];
    }

    try {
      let response = (yield request(`/ember/brands?app_id=${this.appService.app.id}`).then((r) =>
        r.json(),
      )) as BrandWireFormat[];
      this.brands = response.map((wireFormat) => ({
        id: wireFormat.id,
        name: wireFormat.name,
        logoId: wireFormat.logo_id,
        logoUrl: wireFormat.logo_url,
        helpCenterId: wireFormat.help_center_id,
        senderEmailAddressSettingsId: wireFormat.sender_email_address_settings_id,
        isDefault: wireFormat.is_default,
      }));

      return this.brands;
    } catch (error) {
      console.error('loadBrands() error', error);
      this.notificationsService.notifyResponseError(error, {
        default: this.intl.t('standalone.zendesk.settings.brands.failed-to-load-brands'),
      });
      return [];
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Zendesk::Settings::Brands': typeof ZendeskBrands;
    'standalone/zendesk/settings/brands': typeof ZendeskBrands;
  }
}
